/**
 * Saisie multi-lots pour SADAGAS Réception.
 *
 * Variante de InventoryTournant/LotsDialog adaptée à la réception :
 *  - pas de notion de "stock théorique" (on enregistre simplement ce qui arrive)
 *  - boutons 🎤 Voix et 📷 OCR caméra sur chaque date de péremption
 *
 * Props :
 *  - line: ligne de réception (avec qty_validated, lots existants éventuels)
 *  - onSave(lots: Array<{qty, expiration_date, lot_number}>) => Promise
 *  - onClose()
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Layers, Mic, Camera } from 'lucide-react';
import { Button } from '../../ui/button';
import VoiceSaisieDialog from '../InventoryTournant/VoiceSaisieDialog';
import ExpirationOcrDialog from '../InventoryTournant/ExpirationOcrDialog';

const blankLot = () => ({ qty: '', expiration_date: '', lot_number: '' });

const ReceptionLotsDialog = ({ line, onSave, onClose }) => {
  const initial = Array.isArray(line?.lots) && line.lots.length
    ? line.lots.map((l) => ({
        qty: String(l.qty ?? ''),
        expiration_date: l.expiration_date || '',
        lot_number: l.lot_number || '',
      }))
    : [{ qty: String(line?.qty_validated ?? line?.detected_qty ?? ''), expiration_date: '', lot_number: '' }];
  const [lots, setLots] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [voiceLotIdx, setVoiceLotIdx] = useState(null); // idx du lot ciblé par la voix
  const [ocrLotIdx, setOcrLotIdx] = useState(null);     // idx du lot ciblé par l'OCR

  const updateLot = (idx, field, val) => {
    setLots((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l)));
  };

  const addLot = () => setLots((prev) => [...prev, blankLot()]);
  const removeLot = (idx) => setLots((prev) => prev.filter((_, i) => i !== idx));

  // Convertit "MM/YYYY" ou "YYYY-MM-DD" du parser en YYYY-MM-DD pour input[type=date]
  const normalizeIsoDate = (raw) => {
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = raw.match(/^(\d{2})\/(\d{4})$/);
    if (m) return `${m[2]}-${m[1]}-01`;
    return raw;
  };

  const total = lots.reduce((acc, l) => acc + (parseInt(l.qty, 10) || 0), 0);

  const submit = async () => {
    setError(null);
    const payload = [];
    for (const l of lots) {
      const qty = parseInt(l.qty, 10);
      if (!l.qty && !l.expiration_date && !l.lot_number) continue;
      if (Number.isNaN(qty) || qty < 0) {
        setError('Chaque lot doit avoir une quantité ≥ 0');
        return;
      }
      const lotEntry = { qty };
      if (l.expiration_date) lotEntry.expiration_date = l.expiration_date;
      if (l.lot_number?.trim()) lotEntry.lot_number = l.lot_number.trim();
      payload.push(lotEntry);
    }
    if (!payload.length) {
      setError('Ajoutez au moins un lot');
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e?.message || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="reception-lots-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            <h2 className="font-semibold">Lots & péremption</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="reception-lots-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3">
          {line && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Produit reçu</p>
              <p className="text-sm font-semibold text-zinc-900">{line.designation || '—'}</p>
              <p className="text-xs text-zinc-500">
                Qté livrée : <strong>{line.qty_validated ?? line.detected_qty ?? 0}</strong>
                {(line.cip || line.ean) && <> · Code <span className="font-mono">{line.cip || line.ean}</span></>}
              </p>
            </div>
          )}

          <p className="text-sm sm:text-xs text-zinc-600 leading-relaxed">
            Si la livraison contient <strong>plusieurs dates de péremption</strong>, créez un lot par date.
            Utilisez le micro 🎤 pour dicter la date ou la caméra 📷 pour scanner l'étiquette.
          </p>

          <div className="space-y-3">
            {lots.map((lot, idx) => (
              <div
                key={idx}
                className="rounded-lg border-2 border-zinc-200 bg-white p-3 sm:p-2.5"
                data-testid={`reception-lot-row-${idx}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm sm:text-xs font-semibold text-zinc-700">Lot {idx + 1}</span>
                  {lots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLot(idx)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg active:bg-red-100"
                      data-testid={`reception-lot-delete-${idx}`}
                      aria-label="Supprimer ce lot"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className="space-y-2 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
                  {/* Quantité */}
                  <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide">
                    Quantité
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={lot.qty}
                      onChange={(e) => updateLot(idx, 'qty', e.target.value)}
                      className="mt-1 w-full text-base h-12 sm:h-10 border-2 border-zinc-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-semibold"
                      placeholder="0"
                      data-testid={`reception-lot-qty-${idx}`}
                    />
                  </label>
                  {/* Péremption + actions voix/OCR */}
                  <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">
                    Date de péremption
                    <div className="mt-1 flex gap-2 items-stretch">
                      <input
                        type="date"
                        value={lot.expiration_date}
                        onChange={(e) => updateLot(idx, 'expiration_date', e.target.value)}
                        className="flex-1 min-w-0 text-base h-12 sm:h-10 border-2 border-zinc-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        data-testid={`reception-lot-date-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setVoiceLotIdx(idx)}
                        className="h-12 w-12 sm:h-10 sm:w-10 flex items-center justify-center rounded-lg bg-purple-100 text-[#5B3B8C] active:bg-purple-200 hover:bg-purple-200 border-2 border-purple-200"
                        title="Dicter quantité + péremption"
                        data-testid={`reception-lot-voice-${idx}`}
                        aria-label="Dicter à la voix"
                      >
                        <Mic className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setOcrLotIdx(idx)}
                        className="h-12 w-12 sm:h-10 sm:w-10 flex items-center justify-center rounded-lg bg-teal-100 text-teal-700 active:bg-teal-200 hover:bg-teal-200 border-2 border-teal-200"
                        title="Scanner la date (caméra)"
                        data-testid={`reception-lot-ocr-${idx}`}
                        aria-label="Scanner avec la caméra"
                      >
                        <Camera className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  {/* N° lot fournisseur */}
                  <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide sm:col-span-2">
                    N° lot fournisseur <span className="text-zinc-400 normal-case font-normal">(facultatif)</span>
                    <input
                      type="text"
                      value={lot.lot_number}
                      onChange={(e) => updateLot(idx, 'lot_number', e.target.value)}
                      className="mt-1 w-full text-base h-12 sm:h-10 border-2 border-zinc-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="ex: ABC123"
                      data-testid={`reception-lot-number-${idx}`}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addLot}
            className="w-full text-base sm:text-sm font-semibold border-2 border-dashed border-teal-300 text-teal-700 rounded-lg py-3 sm:py-2 hover:bg-teal-50 active:bg-teal-100 flex items-center justify-center gap-2"
            data-testid="reception-lots-add"
          >
            <Plus className="w-5 h-5" /> Ajouter un lot
          </button>

          <div className="rounded-lg bg-teal-50 border-2 border-teal-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-zinc-700">Total des lots</span>
              <span className="text-2xl font-bold text-teal-700" data-testid="reception-lots-total">{total}</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="shrink-0 sticky bottom-0 border-t border-zinc-200 bg-white px-4 py-3 flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-12 sm:h-10 text-base sm:text-sm"
            data-testid="reception-lots-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="flex-1 h-12 sm:h-10 text-base sm:text-sm bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 font-semibold"
            data-testid="reception-lots-save"
          >
            {saving ? 'Enregistrement…' : `Enregistrer ${total > 0 ? `(${total} unités)` : ''}`}
          </Button>
        </div>
      </div>

      {/* Sous-dialogs : voix et OCR caméra */}
      {voiceLotIdx !== null && (
        <VoiceSaisieDialog
          line={{ product_name: line?.designation, ean: line?.ean }}
          onApply={({ quantity, expiration }) => {
            if (quantity !== null && quantity !== undefined) {
              updateLot(voiceLotIdx, 'qty', String(quantity));
            }
            if (expiration) {
              updateLot(voiceLotIdx, 'expiration_date', normalizeIsoDate(expiration));
            }
            setVoiceLotIdx(null);
          }}
          onClose={() => setVoiceLotIdx(null)}
        />
      )}
      {ocrLotIdx !== null && (
        <ExpirationOcrDialog
          onDetect={(isoDate) => {
            if (isoDate) {
              updateLot(ocrLotIdx, 'expiration_date', normalizeIsoDate(isoDate));
            }
            setOcrLotIdx(null);
          }}
          onClose={() => setOcrLotIdx(null)}
        />
      )}
    </div>,
    document.body
  );
};

export default ReceptionLotsDialog;
